import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { works } from "@/mocks/works";

const featured = works.filter((w) => w.featured);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const count = featured.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + count) % count), [count]);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(next, 6000);
    return () => window.clearInterval(timer);
  }, [next, paused]);

  const slide = featured[index];

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ height: "clamp(560px, 86vh, 780px)" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (delta > 50) prev();
        if (delta < -50) next();
        touchStartX.current = null;
      }}
    >
      {featured.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={item.still}
            alt={`${item.title} 剧照`}
            title={`${item.title} - 朝桐光影视作品`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/92 via-primary-950/70 to-primary-950/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-transparent to-primary-950/45" />

      <div className="relative z-10 mx-auto flex h-full w-full max-w-page flex-col justify-between px-4 pb-20 pt-24 md:px-6 md:pb-24 md:pt-28">
        <h1 className="flex items-center gap-2 text-sm font-semibold tracking-wide text-accent-400 md:text-base">
          <span className="h-1 w-6 rounded-full bg-accent-500" />
          <span className="text-background-50">朝桐光</span>
          <span className="text-background-300">· 官方影视资讯与作品展示</span>
        </h1>

        <div>
          <div key={slide.id} className="max-w-2xl animate-fade-up">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
                <i className="ri-fire-fill"></i>
                重磅推荐
              </span>
              <span className="rounded-full border border-background-50/30 px-3 py-1 text-xs font-medium text-background-100">
                {slide.year} · {slide.type}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-background-50/30 px-3 py-1 text-xs font-medium text-background-100">
                <i className="ri-star-fill text-accent-400"></i>
                {slide.rating.toFixed(1)}
              </span>
            </div>

            <h2 className="font-heading text-4xl font-black leading-tight text-background-50 md:text-6xl">
              {slide.title}
            </h2>

            <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
              {slide.synopsis.slice(0, 88)}…
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-300">
              <span>导演 {slide.director}</span>
              <span className="hidden h-3 w-px bg-background-50/30 sm:block" />
              <span>主演 {slide.cast.slice(0, 3).join(" / ")}</span>
              <span className="hidden h-3 w-px bg-background-50/30 sm:block" />
              <span>{slide.genre.join(" · ")}</span>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={`/works/${slide.id}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-fill"></i>
                查看作品详情
              </Link>
              <Link
                to="/works"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-background-50/10"
              >
                浏览全部作品
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          <div className="mt-10 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {featured.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  aria-label={`切换到 ${item.title}`}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"
                  }`}
                />
              ))}
            </div>
            <div className="hidden items-center gap-2 sm:flex">
              <button
                type="button"
                onClick={prev}
                aria-label="上一张"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/30 text-xl text-background-50 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="下一张"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/30 text-xl text-background-50 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}